import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const DEFAULT_FOOTER_TAGLINE = 'Managed by CPRawat Softwares';

const PlatformBrandingContext = createContext({
  branding: null,
  refreshPlatformBranding: async () => {},
  patchPlatformBranding: () => {},
});

function normalizeBranding(data, version = Date.now()) {
  if (!data || typeof data !== 'object') return null;
  return {
    logo: data.logo || null,
    name: data.name || '',
    footer_tagline: data.footer_tagline || DEFAULT_FOOTER_TAGLINE,
    version,
  };
}

export function PlatformBrandingProvider({ children }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState(null);

  const shouldLoadPlatformBranding = Boolean(
    user && (user.role_slug === 'super_admin' || !user.school_id)
  );

  const patchPlatformBranding = useCallback((patch) => {
    if (!patch || typeof patch !== 'object') return;
    setBranding((prev) => {
      const base = prev || {
        logo: null,
        name: '',
        footer_tagline: DEFAULT_FOOTER_TAGLINE,
        version: 0,
      };
      return {
        ...base,
        ...patch,
        version: Date.now(),
      };
    });
  }, []);

  const refreshPlatformBranding = useCallback(async () => {
    if (!user || !shouldLoadPlatformBranding) {
      setBranding(null);
      return;
    }
    try {
      const res = await api.get('platform/settings');
      setBranding(normalizeBranding(res.data?.data));
    } catch {
      setBranding(null);
    }
  }, [user, shouldLoadPlatformBranding]);

  useEffect(() => {
    refreshPlatformBranding();
  }, [refreshPlatformBranding]);

  return (
    <PlatformBrandingContext.Provider
      value={{ branding, refreshPlatformBranding, patchPlatformBranding }}
    >
      {children}
    </PlatformBrandingContext.Provider>
  );
}

export function usePlatformBranding() {
  return useContext(PlatformBrandingContext);
}

export { DEFAULT_FOOTER_TAGLINE };
